import React, { useState, useEffect } from 'react';
import { GetRequest } from 'components/common';
import { compatibleMaterials } from 'components/frank/utils/materialCompatibility.js';

// The /mcc "Malzeme" row dropdown: when the owner has MORE THAN ONE material that fits the section, it lets them
// choose WHICH one prices the manufacturing lines, instead of the server's default lowest-id auto-pick. It sits
// beside the "Yönet" button and always shows the material the lines are actually calculated with.
//
// DYNAMIC ON BOBBIN. A carton material row is a bobbin-roll row or a folded-sheet row (its bobbin_enabled flag),
// and a solution is billed against the row that matches how it is produced -- so the options, and the shown
// selection, follow the section's current bobbin state: toggle Bobin and the list re-filters and re-picks at
// once, mirroring Frank::MaterialScores::CartonMaterialUserPrice server-side. A key with no bobbin flag (every
// non-carton material) has no such split, so every row is compatible.
//
// selectedId is the owner's EXPLICIT choice (a UserUnitPrice id) or null for "no choice yet" -- which shows, and
// prices against, the lowest-id compatible row (the server's auto-pick), so an existing quote is unchanged until
// the owner actually picks. onSelect(id) reports a pick up; the parent sends it as material_user_price_id.
//
// `materials` is the section's own material rows. The parent (ManufacturingLineLists) already fetches them for its
// own add/manage button + gate, so it passes them in and this does NOT fetch again -- one round-trip, one source of
// truth. It is undefined ONLY when the component is used standalone (its unit tests); then it fetches for itself.
const MaterialSelect = ({ materialKey, bobbinEnabled, boardThicknessMm, selectedId, onSelect, materials: materialsProp }) => {
  const [fetchedMaterials, setFetchedMaterials] = useState(null);

  // Standalone fallback only (materialsProp === undefined). NOT useCache: the list must be fresh, and the component
  // remounts on frank:own-machines-changed (a material add / edit / remove); GetRequest's useCache store is never
  // invalidated by a material save, so caching would keep serving the old list -- hiding the dropdown, or a
  // just-added row. When the parent supplies `materials` this effect does nothing (the parent owns the fetch).
  useEffect(() => {
    if (materialsProp !== undefined) { return; }
    if (!materialKey) { setFetchedMaterials(null); return; }
    GetRequest({
      url: `/quote-builder/user_unit_prices/list/section/${materialKey}/${window.locale}`,
      callback: (data) => { setFetchedMaterials(Array.isArray(data) ? data : []); },
    });
  }, [materialKey, materialsProp]);

  const materials = materialsProp !== undefined ? materialsProp : fetchedMaterials;
  if (!materials) { return null; }

  // Rows ELIGIBLE for how this section is produced, so the shown/offered rows are the ones that will actually be
  // billed -- the same reading the server-side material score makes, and the SAME test the add/manage button and
  // the "add a material first" gate read (materialCompatibility): carton splits on the bobbin flag, hard cardboard
  // on the dieline board thickness (empty until it is known, so the dropdown stays hidden rather than offer a pick
  // that re-prices the box against another board), every other key has no such split.
  const compatible = compatibleMaterials(materials, { materialKey, bobbinEnabled, boardThicknessMm });

  // A choice worth a dropdown only exists when there is more than one row to choose between.
  if (compatible.length < 2) { return null; }

  // The server auto-picks the lowest-id compatible row when nothing is chosen; mirror that for the shown value so
  // the dropdown always reads "these lines are calculated with THIS material". An explicit choice wins while it
  // still fits; once it does not (a bobbin toggle drops it from `compatible`), the shown value falls back to the
  // auto-pick, exactly as the server does.
  const autoPickId = compatible.reduce((lowest, material) => (material.id < lowest ? material.id : lowest), compatible[0].id);
  const chosenIsCompatible = compatible.some((material) => material.id === selectedId);
  const shownValue = chosenIsCompatible ? selectedId : autoPickId;

  return (
    <select
      data-material-select={materialKey}
      value={shownValue}
      onChange={(event) => onSelect(Number(event.target.value))}
      className="form-control"
      style={{ maxWidth: '260px', height: '38px' }}
      aria-label={t('quote_builder.material')}
    >
      {compatible.map((material) => (
        <option key={material.id} value={material.id}>{material.description}</option>
      ))}
    </select>
  );
};

export default MaterialSelect;
